import { ChangeDetectorRef, Component, ElementRef, HostListener, Input, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import * as ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { RemmiDateRangeComponent } from '@common/remmi-date-range/remmi-date-range.component';
import { commonImages } from '@images/common-images';
import { VoiceRecognitionService } from '@modules/rmi-notes/voice-recognition.service';
import { ApiService } from '@services/api/api.service';
import { CalendarAPIService } from '@services/api/calendarAPI.service';
import { PrecinctScopeService } from '@services/api/precinct-scope.service';
import { Crypto } from '@services/helpers/encryption-utils';
import { LocalStorageService } from '@services/helpers/local-storage.service';
import { LeadManagementService } from '@services/lm-service/lead-management.service';
import { ReduxService } from '@state-management/redux.service';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { OverlayPanel } from 'primeng/overlaypanel';
import { Observable, skip, Subject, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ProjectAppointmentCreateComponent } from '../project-appointment-create/project-appointment-create.component';
import { MessageService } from '@services/util/message.service';
import { DuplicateContactDraft } from '@modules/contact/possible-duplicate-contact/possible-duplicate-contact.component';
import { RemmiConfirmService } from '@common/remmi-confirm/remmi-confirm.service';

@Component({
  selector: 'app-project-appointment-list',
  templateUrl: './project-appointment-list.component.html',
  styleUrls: ['./project-appointment-list.component.scss'],
})
export class ProjectAppointmentListComponent {
  @ViewChild('inspectionModel') inspectionModel: ProjectAppointmentCreateComponent;
  @ViewChild('opened') opened: OverlayPanel;
  /** Shared date-range picker — held only so Reset can wipe its label. */
  @ViewChild('dateRangeRef') dateRangeRef?: RemmiDateRangeComponent;
  @Input() client_id: Observable<any> = null;
  @Input() pid: boolean;

  common_images = commonImages;
  public keyword = null;
  public rangeDates = null;
  public projectSearch: string = null;
  public agentSearch = null;
  public appSearch = null;
  public query = null;
  public selectedRows = [];

  /** The current page, as returned by the list API (already filtered, sorted and paged). */
  pagedAppointments: any[] = [];
  /** Rows in the active tab across all pages (`pagination.total`). */
  totalRecords = 0;
  /** Guards against an older, slower response overwriting a newer filter's results. */
  private listRequestId = 0;
  private searchTimer: any;
  activeTab: AppointmentTab = 'all';
  tabCounts: Record<AppointmentStatus, number> = { upcoming: 0, overdue: 0, completed: 0 };
  tabs: { value: AppointmentTab; label: string }[] = [
    { value: 'all', label: 'All Appointments' },
    { value: 'upcoming', label: 'Upcoming' },
    { value: 'overdue', label: 'Overdue / Needs Update' },
    { value: 'completed', label: 'Completed' },
  ];
  sortDirection: 'asc' | 'desc' = 'asc';
  pageIndex = 0;
  pageSize = 10;
  pageSizeOptions = [10, 25, 50, 100];
  projectOptions: { id: string; name: string }[] = [];
  // 'all' while the period dropdown is hidden: with no visible "This Month" label, month-only
  // cards (e.g. Overdue 2) read as wrong next to the all-dates tab badges (Overdue 86).
  statsPeriod: StatsPeriod = 'all';
  statsPeriodOptions: { value: StatsPeriod; label: string }[] = [
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'year', label: 'This Year' },
    { value: 'all', label: 'All Time' },
  ];
  stats = { total: 0, upcoming: 0, overdue: 0, completed: 0 };
  // Hidden for now at the client's request — the logic behind both stays wired, so turning
  // them back on is just flipping these flags.
  isStatsPeriodVisible = false;
  isProjectFilterVisible = false;

  originalAssigns: any;
  /** Staff id → full name, for the Created column. */
  staffNames = new Map<string, string>();
  propertyModel: boolean;
  contactModel: boolean;
  isModel: boolean = false;
  registerModel: boolean;
  register_property: FormGroup;
  attendee_liked: FormGroup;
  public Editor = ClassicEditor;
  S3Path = environment.S3 + '/';
  isRecording: any;
  submit: boolean;
  inputValue: string = '';
  isRecord: boolean = false;
  keywordTags = '';
  todo: any[] = [];
  new_tag = [];
  tag_type = null;
  module_type = null;
  newTagTypePopup = false;
  preference_tags: any[] = [];
  preference_tags_copy: any[] = [];
  module_Id: any;
  new_tag_popup = false;
  contactConflict: boolean = false;
  cContacts: any = [];
  conflictLeadId: string = null;
  duplicateContactDraft: DuplicateContactDraft = {};
  updateRegister_user: any;
  isExistPrompt: boolean = false;
  insertedLead_id: any;
  register_submit: boolean = false;
  contact_related: any;
  store_contact_details = null;
  contactNote = null;
  lead_notes: any[] = [];
  listing_details: any;
  listing_details_copy: any;
  ofi_id: any;
  cancelled_app: any[] = [];
  modelTitle: any;
  appointment_details = null;
  app_idx: any;
  unit_list: any[] = [];
  appointment_type: any[] = [];
  public likDislike_icon = null;
  timeStamp: any;
  project_id: any;
  client_idx: any;

  constructor(
    private api: ApiService,
    private fb: FormBuilder,
    private voiceRecognition: VoiceRecognitionService,
    private toaster: ToastrService,
    public lmService: LeadManagementService,
    private localStorageService: LocalStorageService,
    private redux: ReduxService,
    private changeDetect: ChangeDetectorRef,
    private _CalendarAPIService: CalendarAPIService,
    private message: MessageService,
    public precinctScope: PrecinctScopeService,
    private remmiConfirm: RemmiConfirmService
  ) {
    this.register_property = this.fb.group({
      first_name: [null, Validators.required],
      last_name: [''],
      mobile_no: [null, Validators.required],
      email: [null, [Validators.required, Validators.email]],
      project_details: [''],
      send_link: [false],
      note: [''],
      attendee: [false],
      like_dislike: [null],
      contactid: [null],
    });
    this.attendee_liked = this.fb.group({
      appt_status: [''],
      appt_note: [''],
      lots: [''],
    });
  }

  /**
   * Initializes the component by setting up necessary variables and fetching required data.
   * - Retrieves the project ID from local storage.
   * - Sets the current timestamp.
   * - Fetches agents, project tags, appointments, and units.
   * - Subscribes to `client_id` and updates `client_idx`, triggering an appointment fetch.
   */
  ngOnInit(): void {
    // Retrieve the project ID from local storage
    this.project_id = Crypto.decrypt(this.localStorageService.GetItem('pid'));
    this.GetProjectList(this.project_id);

    // Set the current timestamp
    this.timeStamp = new Date();

    // Fetch the list of agents
    this.getAgents();

    // Subscribe to `client_id` changes and update `client_idx`, then fetch appointments
    this.clientIdSub = this.client_id?.subscribe(rex => {
      this.client_idx = rex || '';
      this.getAppointment();
    });

    // Fetch project tags
    this.getProjectTags();

    // Fetch appointments (called here separately as well)
    this.getAppointment();

    // Fetch the list of units
    this.getUnits();

    // skip(1) because the stream replays on subscribe and getAppointment() above covers it.
    this.precinctScopeSub = this.precinctScope.scopedProjectIds$.pipe(skip(1)).subscribe(() => this.getAppointment());
  }

  /** Re-fetch when the user switches between "This project only" and "Entire precinct". */
  private precinctScopeSub?: Subscription;
  private clientIdSub?: Subscription;

  ngOnDestroy(): void {
    this.precinctScopeSub?.unsubscribe();
    this.clientIdSub?.unsubscribe();
    clearInterval(this.isRecording);
    clearTimeout(this.searchTimer);
    this.lmService.tag_manager = false;
  }

  ProjectList: any[] = [];
  // Fetches all projects and filters the list to match the provided project ID
  GetProjectList(id) {
    this.api.get(`/projects-name-list?office_id=${this.api.getOfficeId()}`).subscribe((response: any) => {
      const { status, data } = response;
      if (status) {
        this.ProjectList = data?.find(el => el?._id === id);
      }
    });
  }

  /**
   * Fetches project tags from the Redux store and processes them.
   * - Retrieves tags for the current office and project.
   * - Sets the `module_Id` to the first tag's `_id`.
   * - Filters preference tags based on the `project_id` and marks them as open.
   * - Adds a "+ Create New" option to `preference_tags`.
   * - Copies `preference_tags` to `preference_tags_copy`.
   * - Iterates through tags to extract appointment types.
   */
  getProjectTags() {
    this.redux.getTags(this.api.getOfficeId(), true, 'project').subscribe({
      next: response => {
        this.module_Id = response?.data[0]?._id;
        let allTags = [...response?.data];

        // Extract and filter preference tags related to the current project
        this.preference_tags = [...response?.data[0]?.types]
          ?.filter(el => el?.project_id === this.project_id)
          ?.map(el => {
            return { ...el, isOpen: true };
          });

        // Add a "+ Create New" option
        this.preference_tags.push({ name: '+ Create New', _id: 'createnew' });

        // Store a copy of preference tags
        this.preference_tags_copy = this.preference_tags;

        // Extract appointment types from tags
        allTags?.forEach(x => {
          x.types?.forEach(y => {
            if (y?.slug == 'appointment-type') {
              this.appointment_type = y?.data;
            }
          });
        });
      },
    });
  }

  /**
   * Fetches units (apartments) for the current project.
   * - Calls the API to get the list of apartments.
   * - Stores the retrieved data in `unit_list`.
   */
  getUnits() {
    this.api.get(`/project/apartments/${this.project_id}`).subscribe({
      next: response => {
        this.unit_list = response?.data;
      },
    });
  }

  /**
   * Handles unit selection by updating the `todo` list.
   * - Filters selected units based on the user's choices.
   * - Extracts preference tags from selected units and updates `todo`.
   * - Restricts selection to a maximum of 3 units and shows an error message if exceeded.
   */
  selectUnit() {
    const selectedLots = this.attendee_liked.controls['lots'].value;
    // this.todo = [];

    // Filter selected units
    let item = this.unit_list?.filter(el => selectedLots?.toString()?.includes(el?._id));

    // Collect preference tags from selected units
    item?.forEach(el => {
      if (el?.preference_tag?.length > 0) {
        this.todo = [...new Set([...this.todo, ...el?.preference_tag])];
      }
    });

    if (selectedLots.length > 3) {
      this.attendee_liked.controls['lots'].setValue(selectedLots.slice(0, 3));
      this.toaster.error('You can select up to three items');
      return false;
    }
  }

  /**
   * Escape closes the attendance popup. The decorator used to sit on the `unit_list` field, so
   * Escape called a non-function and the popup never closed.
   */
  @HostListener('document:keydown.escape')
  handleKeyboardEvent() {
    this.opened?.hide();
  }

  /**
   * Displays appointment details and updates related fields.
   * - Shows the attendance popup next to the clicked cell.
   * - Updates `app_idx` with the selected appointment ID.
   * - Patches form values with appointment details (lots, note, status).
   * - Sets `todo` list based on appointment preference tags.
   * - Updates `likDislike_icon` based on total likes or dislikes.
   * @param appt The selected appointment object.
   * @param event The event that triggered this function.
   */
  getApptDetails(item: any, event: Event) {
    this.opened.show(event);

    this.app_idx = item?._id;

    // Patch form values with appointment data
    this.attendee_liked.patchValue({
      lots: item?.lots,
      appt_note: item?.appt_note,
      // Saved value once the API returns it (docs/backend/project-appointments-list-api.md §3a);
      // until then it is re-derived from the side effects the save leaves behind.
      appt_status:
        item?.appt_status ||
        (item?.is_cancelled === true
          ? 'cancelled'
          : item?.edited === true
            ? 'rescheduled'
            : item?.total_attendee?.length > 0
              ? 'completed'
              : 'pending'),
    });

    // Update the todo list with preference tags if available
    this.todo = item?.preference_tag?.length > 0 ? item?.preference_tag : [];

    // Set like/dislike icon based on likes and dislikes count
    this.likDislike_icon = item?.total_likes?.length > 0 ? 'pi pi-thumbs-up' : item?.total_dislikes?.length > 0 ? 'pi pi-thumbs-down' : null;

    this.changeDetect.detectChanges();
  }

  /**
   * Saves attendee like/dislike status along with appointment details.
   * - Prepares the form data with selected preferences and status.
   * - Sends a POST request to save the data.
   * - Shows success notification and refreshes appointment list on success.
   */
  saveAttendLike() {
    let form = this.attendee_liked.value;

    // Determine like/dislike status
    form['like_dislike'] = this.likDislike_icon === 'pi pi-thumbs-up' ? true : this.likDislike_icon === 'pi pi-thumbs-down' ? false : null;

    form['appt_id'] = this.app_idx;

    // Include preference tags if available
    form['preference_tag'] = this.todo?.length > 0 ? this.todo : [];

    // Send the form data to the API
    this.api.post('/project-appt-info', form).subscribe({
      next: _ => {
        this.toaster.success('Saved successfully');
        this.getAppointment();
        this.attendee_liked.reset();
      },
    });
  }

  /**
   * Updates the registration form with selected contact details.
   * - Fills in the first name, email, mobile number, and contact ID.
   * @param e The selected contact object.
   */
  contactSelection(e) {
    this.register_property.patchValue({
      first_name: e?.name,
      email: e?.email,
      mobile_no: e?.mobile_no ? e?.mobile_no : this.updateRegister_user?.mobile_no || null,
      contactid: e?._id,
    });
  }

  /**
   * Opens the inspection model for creating or updating an appointment.
   * - Updates `modelTitle` based on whether an ID is provided.
   * - Triggers the observable `app_id` with the given appointment ID.
   * - Sets `isModel` to `true` and displays the inspection modal.
   * @param id The appointment ID (if updating an existing appointment).
   */
  openInspection(id = null) {
    // The list API also returns contact-only clients (`is_contact`, `_id` = contact id). The edit
    // form re-posts its clients through the lead flow, which would turn them into new leads —
    // fetch-project-events never sent them, so keep the form's client list lead-only as before.
    this.appointment_details = id ? { ...id, project: id?.projectid, clients: id?.clients?.filter(client => !client?.is_contact) ?? [] } : null;
    this.modelTitle = id !== null ? 'Update Appointment' : 'Create Appointment';
    this.isModel = true;
    // this.inspectionModel.show();
  }

  /**
   * Closes the inspection model and refreshes appointments if `e` is `true`.
   * - Closes the modal.
   * - Calls `getAppointment()` to refresh data.
   * - Updates `isModel` state.
   * @param e A boolean indicating whether to close the modal.
   */
  closeModel(e) {
    if (e === true) {
      // this.inspectionModel.close();
      // this.isModel = false;
      this.getAppointment();
    } else {
      this.inspectionModel.close();
      this.isModel = false;
      this.getAppointment();
    }
  }

  /**
   * Formats a given date-time string into a 12-hour format with AM/PM.
   * @param dateTime The date-time string to format.
   * @returns The formatted time string (e.g., "10:30 AM").
   */

  getTimeFormat(dateTime) {
    return moment(dateTime).format('hh:mm A');
  }

  /**
   * Fetches the list of office staff (agents).
   * - Constructs an API URL using user and office IDs.
   * - Calls `getSetProjectInfo` to fetch staff details.
   * - Stores a deep copy of the response data in `originalAssigns`.
   */
  getAgents() {
    let url = `/users/office-staff-list?uid=${this.api.getUserId()}&officeid=${this.api.getOfficeId()}&show=staff`;
    this.redux.getSetProjectInfo(true, 'office_staff', url).subscribe({
      next: response => {
        this.originalAssigns = JSON.parse(JSON.stringify(response.data));
        this.staffNames = new Map(this.originalAssigns?.map(staff => [String(staff?._id), staff?.full_name]) ?? []);
      },
    });
  }

  /** Collects the ids of checked rows on the current page for Bulk Edit. */
  cancelApp() {
    this.cancelled_app = this.pagedAppointments.filter(appt => appt?.is_checked === true).map(appt => appt?._id);
  }

  /**
   * Cancels selected appointments.
   * - Sends an API request to mark selected appointments as cancelled.
   * - Clears `cancelled_app` after a successful response.
   * - Displays a success message and refreshes appointments.
   */
  cancelledAppointment() {
    let data = {
      appts_ids: this.cancelled_app?.length > 0 ? this.cancelled_app : [this.ofi_id],
      is_cancelled: true,
    };

    this.api.post('/calendar/cancel-project-appt', data).subscribe({
      next: response => {
        this.propertyModel = false;
        this.cancelled_app = [];
        this.toaster.success(response.message);
        this.getAppointment();
      },
      error: () => {
        this.toaster.error('Something went wrong');
      },
    });
  }

  /**
   * Marks selected appointments as attended.
   * - Sends an API request to update appointment attendance status.
   * - Clears `cancelled_app` after a successful response.
   * - Displays a success message and refreshes appointments.
   */
  attendedAppointment() {
    let data = {
      appts_ids: this.cancelled_app?.length > 0 ? this.cancelled_app : [this.ofi_id],
    };

    this.api.post('/calendar/attendee-project-appt', data).subscribe({
      next: response => {
        this.propertyModel = false;
        this.cancelled_app = [];
        this.toaster.success(response.message);
        this.getAppointment();
      },
      error: () => {
        this.toaster.error('Something went wrong');
      },
    });
  }

  /**
   * Loads one page from the list API (docs/backend/project-appointments-list-api.md). The
   * server applies role visibility, drops cancelled rows, filters, sorts, pages and counts —
   * every toolbar/tab/pager change just calls this again.
   */
  getAppointment() {
    const requestId = ++this.listRequestId;
    this.api.get('/calendar/project-appointments-list', this.buildListParams()).subscribe({
      next: response => {
        // A newer filter/page request was sent while this one was in flight.
        if (requestId !== this.listRequestId) return;
        this.applyServerPage(response);
      },
      error: error => {
        if (requestId !== this.listRequestId) return;
        this.applyServerPage(null);
        // 422s carry a specific reason (e.g. "project_id is required") — show it.
        this.toaster.error(error?.error?.message || 'Something went wrong');
      },
    });
  }

  /** Adds the display-only fields the table reads. */
  private decorateAppointment(appt: any): any {
    return {
      ...appt,
      is_checked: false,
      tooltip: appt?.edited ? 'Rescheduled' : appt?.total_attendee?.length > 0 ? 'Attended' : 'Pending',
      status: appt?.status ?? this.getAppointmentStatus(appt),
      projectLabel: appt?.project?.Project_Name || appt?.project_name || this.ProjectList?.['Project_Name'] || '',
      createdAt: appt?.created_at ? new Date(appt.created_at) : this.getCreatedAt(appt?._id),
      createdByName: appt?.created_by_user?.full_name ?? '',
      typeClass: this.getTypeClass(appt?.app_type),
      agentInitials: this.getInitials(appt?.agents?.[0]?.full_name),
    };
  }

  /** Query params of the list API — one per toolbar control (doc §2 / §6). Empty values are dropped. */
  private buildListParams(): Record<string, string> {
    const [from, to] = this.rangeDates ?? [];
    const params: Record<string, any> = {
      office_id: this.api.getOfficeId(),
      project_id: this.pid === false ? '' : this.project_id,
      project_ids: this.precinctScope.scopedProjectIdsParam(),
      contact_id: this.client_idx,
      search: this.keyword?.toString()?.trim(),
      filter_project_id: this.projectSearch,
      app_type: this.appSearch,
      agent_ids: this.agentSearch?.join(','),
      from: from ? moment(from).format('YYYY-MM-DD') : '',
      to: from ? moment(to || from).format('YYYY-MM-DD') : '',
      tab: this.activeTab,
      stats_period: this.statsPeriod,
      sort: this.sortDirection === 'asc' ? 'start_asc' : 'start_desc',
      page: this.pageIndex + 1,
      limit: this.pageSize,
    };
    return Object.fromEntries(
      Object.entries(params)
        .filter(([, value]) => value !== '' && value != null)
        .map(([key, value]) => [key, String(value)])
    );
  }

  /** The response already holds one filtered, sorted page plus counts and stats. */
  private applyServerPage(response: any) {
    this.pagedAppointments = (response?.data ?? []).map(appt => this.decorateAppointment(appt));
    this.totalRecords = response?.pagination?.total ?? this.pagedAppointments.length;
    this.tabCounts = {
      upcoming: response?.counts?.upcoming ?? 0,
      overdue: response?.counts?.overdue ?? 0,
      completed: response?.counts?.completed ?? 0,
    };
    this.stats = {
      total: response?.stats?.total ?? 0,
      upcoming: response?.stats?.upcoming ?? 0,
      overdue: response?.stats?.overdue ?? 0,
      completed: response?.stats?.completed ?? 0,
    };
    this.projectOptions = (response?.filter_options?.projects ?? []).map(project => ({ id: String(project?._id), name: project?.Project_Name }));
    this.cancelApp();

    // A delete or filter can leave the user past the last page — step back to it.
    if (!this.pagedAppointments.length && this.pageIndex > 0 && this.totalRecords > 0) {
      this.pageIndex = this.pageCount - 1;
      this.getAppointment();
    }
  }

  /**
   * Deletes an appointment/event.
   * - Sends a request to delete an event by `appt_id`.
   * - Displays a success or error message based on the response.
   * - Refreshes the appointment list after successful deletion.
   * @param itm The appointment item to be deleted.
   */

  appointmentRendor: Subject<Boolean> = new Subject<Boolean>();
  deleteAppointment(itm) {
    this.remmiConfirm
      .delete({ title: 'Delete Appointment?', message: 'This appointment will be removed from the calendar.' })
      .subscribe(isConfirmed => isConfirmed && this.removeAppointment(itm));
  }

  private removeAppointment(itm) {
    let query = {
      user_id: this.api.getUserId(),
      itm: itm,
      appt_id: itm?._id,
    };
    this._CalendarAPIService.removeProjectEventFromCalendarByUserId(query).subscribe({
      next: () => {
        this.toaster.success('Event removed successfully');
        this.cancelled_app = [];
        this.getAppointment(); // Refresh appointment list after deletion
        this.appointmentRendor.next(true);
      },
      error: () => {
        this.toaster.error('Something went wrong');
      },
    });
  }

  /** Reset button — clears every table filter; the active tab and stats period stay as picked. */
  clearSearch() {
    this.keyword = null;
    this.projectSearch = null;
    this.agentSearch = null;
    this.appSearch = null;
    this.rangeDates = null;
    if (this.dateRangeRef) this.dateRangeRef.dateName = '';
    this.message.sendMessage({
      isClearItems: true,
    });
    this.applyFilters();
  }

  /**
   * Applies a preset or custom range from <app-remmi-date-range> ({ name, from, to }).
   * "All" carries no `from`, so it clears the date filter.
   */
  onDateFilter(item: any) {
    this.rangeDates = item?.from ? [item.from, item.to || item.from] : null;
    this.applyFilters();
  }

  /**
   * Fetches project details based on the provided project information.
   * - Retrieves `listing_details` from an API based on `projectid` and `ofi_id`.
   * - Updates form fields with project property address.
   * - Opens the property details modal.
   * @param pro_details The project details object.
   */
  projectDetails(pro_details) {
    // Assign `ofi_id` if the project has an inspection flag
    if (pro_details?.isInspection === true || pro_details?.isInspection === false) {
      this.ofi_id = pro_details?._id;
    }

    this.api.get(`/ofi/project/leads/${pro_details?.projectid}?ofi_id=${this.ofi_id}&office_id=${this.api.getOfficeId()}`).subscribe({
      next: response => {
        this.listing_details = response?.data;
        this.listing_details['projectid'] = response?.data?._id;
        this.listing_details_copy = this.listing_details;
        this.register_property.patchValue({
          project_details: this.listing_details?.property_address,
        });
      },
      error: () => {
        this.toaster.error('Something went wrong');
      },
    });

    this.propertyModel = true; // Open property details modal
  }

  /**
   * Retrieves contact details and related projects for the given contact.
   * - If `contactid` is null, converts lead to contact.
   * - Stores contact details and fetches related projects.
   * - Opens the contact details modal.
   * @param ele The contact element containing contact details.
   */
  contactDetails(ele) {
    this.contactNote = null;
    // Store lead notes if available, otherwise set to an empty array
    ele?.lead_notes?.length > 0 ? (this.lead_notes = ele?.lead_notes) : (this.lead_notes = []);

    // Convert to contact if `contactid` is null
    if (ele?.contactid == null) {
      this.duplicateContactDraft = {
        firstName: ele?.first_name,
        lastName: ele?.last_name,
        fullName: ele?.full_name,
        email: ele?.email,
        mobile: ele?.mobile_phone ?? ele?.mobile_no,
      };
      this.convertToContact(ele?._id);
      return false;
    }

    this.store_contact_details = ele;

    this.api.get(`/ofi/project/contact-related-projects/${ele?.contactid}?office_id=${this.api.getOfficeId()}&lead_id=${ele?._id}`).subscribe({
      next: response => {
        this.contactModel = true; // Open contact details modal
        this.contact_related = response?.data;
      },
      error: error => {
        this.toaster.error(error?.error?.message);
      },
    });
  }

  /**
   * Deletes a specific note associated with a listing.
   * - Sends a DELETE request to remove the note by its `_id`.
   * - Displays a success message upon successful deletion.
   * - Refreshes project details and lead notes after deletion.
   * @param note The note object to be deleted.
   */
  deleteNotes(note) {
    this.remmiConfirm
      .delete({ title: 'Delete Note?', message: 'This note will be permanently deleted.' })
      .subscribe(isConfirmed => isConfirmed && this.removeNote(note));
  }

  private removeNote(note) {
    this.api.delete(`/ofi/listing/lead-note-remove/${note?._id}?office_id=${this.api.getOfficeId()}`).subscribe({
      next: response => {
        this.toaster.success(response?.message);
        this.projectDetails(this.listing_details); // Refresh project details

        // Delay updating the lead notes after deletion
        setTimeout(() => {
          this.lead_notes = [];
          this.listing_details?.leads?.forEach(element => {
            if (element?._id === this.store_contact_details?._id) {
              this.lead_notes = element?.lead_notes;
            }
          });
        }, 1500);
      },
      error: () => {
        this.toaster.error('Something went wrong');
      },
    });
  }

  /**
   * Updates a specific note for a lead.
   * - Sends a PUT request to update the note content.
   * - Displays a success message upon successful update.
   * - Refreshes project details and lead notes after update.
   * @param note The note object to be updated.
   */

  @ViewChild('custom_Editor1', { static: false }) custom_Editor1!: ElementRef;
  updateNotes(note) {
    let data = {
      note: this.custom_Editor1?.nativeElement['innerHTML'],
    };

    this.api.put(`/ofi/listing/leads-note-update/${note?._id}?office_id=${this.api.getOfficeId()}`, data).subscribe({
      next: response => {
        this.toaster.success(response?.message);
        this.projectDetails(this.listing_details); // Refresh project details

        // Delay updating the lead notes after modification
        setTimeout(() => {
          this.lead_notes = [];
          this.listing_details?.leads?.forEach(element => {
            if (element?._id === this.store_contact_details?._id) {
              this.lead_notes = element?.lead_notes;
            }
          });
        }, 1500);
      },
      error: () => {
        this.toaster.error('Something went wrong');
      },
    });
  }

  /**
   * Updates feedback for a selected column (attendee) in an appointment.
   * - Sends a POST request with appointment ID and lead details.
   * - Displays a success message and refreshes project details.
   * - Refreshes the appointment list.
   * @param contact The contact/lead object.
   */

  selectColumn(contact) {
    let data = {
      appt_id: this.ofi_id,
      lead_id: contact?._id,
      attendee: contact?.attendee,
    };
    this.api.post('/project-appt-feedback', data).subscribe({
      next: response => {
        this.toaster.success(response?.message);
        this.projectDetails(this.listing_details);
        this.getAppointment();
      },
      error: () => {
        this.toaster.error('Something went wrong');
      },
    });
  }
  // selectColumn(contact) {
  //   this.api.post('/ofi/listing/checkin', { check_in: [{ _id: contact?._id, attendee: contact?.attendee }] }).subscribe({
  //     next: response => {
  //       this.toaster.success(response?.message);
  //       this.projectDetails(this.listing_details); // Refresh project details
  //       this.getAppointment(); // Refresh appointment list
  //     },
  //     error: () => {
  //       this.toaster.error('Something went wrong');
  //     },
  //   });
  // }

  /**
   * Likes or dislikes a lead in an appointment.
   * - Sends a POST request with the lead ID and like/dislike event.
   * - Displays a success message upon successful update.
   * - Refreshes project details and appointment list.
   * @param contact The lead object.
   * @param event The like or dislike status.
   */
  likeDislikeLead(contact, event) {
    let data = {
      appt_id: this.ofi_id,
      lead_id: contact?._id,
      like_dislike: event,
    };

    this.api.post('/project-appt-feedback', data).subscribe({
      next: response => {
        this.toaster.success(response?.message);
        this.projectDetails(this.listing_details);
        this.getAppointment(); // Refresh appointment list
      },
      error: () => {
        this.toaster.error('Something went wrong');
      },
    });
  }

  /**
   * Opens the registration model popup.
   * - Resets the `updateRegister_user` field.
   * - Opens the `registerModel` popup.
   * - Clears and resets the registration form fields.
   * - Prefills project details with the selected listing.
   */
  registerModelPopup() {
    this.updateRegister_user = null;
    this.registerModel = true;

    // Reset form fields
    this.register_property.patchValue({
      first_name: null,
      last_name: null,
      mobile_no: null,
      email: null,
      project_details: null,
      send_link: false,
      note: null,
      attendee: false,
      like_dislike: null,
      lead_category: null,
      lead_status: null,
      lead_type: null,
    });

    this.custom_Editor2.nativeElement['innerHTML'] = '';

    this.message.sendMessage({ isClearItems: true });
    // Prefill project details with the listing name
    this.register_property.patchValue({
      project_details: this.listing_details?.Project_Name,
    });
  }

  /**
   * Opens the registration modal for editing an existing lead.
   * - Populates the form fields with the selected lead's data.
   * - Prefills project details and extracts the first name from full name.
   * @param lead The lead object to be edited.
   */
  editRegister(lead) {
    this.updateRegister_user = lead;
    this.registerModel = true;
    ((this.register_user_note = lead?.lead_notes?.length > 0 ? lead?.lead_notes[0].text : null),
      this.register_property.patchValue(this.updateRegister_user));
    this.register_property.patchValue({
      project_details: this.listing_details?.Project_Name,
      first_name: this.updateRegister_user?.contactid,
      mobile_no: this.updateRegister_user?.mobile_no,
    });
  }

  /**
   * Handles the registration of a user (either create or update).
   * - Validates the form before submission.
   * - Checks for duplicate email before creating a new lead.
   * - Formats data properly before sending an API request.
   * - Creates a new lead or updates an existing one based on the method provided.
   * @param method 'create' for adding a new lead, 'update' for modifying an existing lead.
   */
  register_user_note = null;
  @ViewChild('custom_Editor2', { static: false }) custom_Editor2!: ElementRef;
  userRegister(method) {
    if (!this.register_property.valid) {
      this.register_submit = true;
      return false;
    }

    if (method === 'create') {
      // Check if the email already exists in the listing details
      let isExist = this.listing_details?.leads?.find(el => el?.email == this.register_property.controls['email'].value);
      if (isExist) {
        this.isExistPrompt = true;
        return false;
      }

      let check_name = this.register_property.controls['first_name'].value?.split(' ');
      let data = {
        first_name: check_name?.length > 0 ? check_name[0] : this.register_property.controls['first_name'].value || '',
        last_name: check_name?.length > 1 ? check_name[1] : '',
        mobile_no: this.register_property.controls['mobile_no'].value,
        email: this.register_property.controls['email'].value,
        project_details: this.register_property.controls['project_details'].value,
        send_link: false,
        note: this.custom_Editor2?.nativeElement['innerHTML'],
        attendee: false,
        like_dislike: null,
        lead_category: 'project',
        lead_status: 'New',
        lead_type: 'Buyer',
        project: this.listing_details?.projectid ? this.listing_details?.projectid : this.listing_details?._id,
        office_id: this.api.getOfficeId(),
        contactid: this.register_property.controls['contactid'].value ? this.register_property.controls['contactid'].value : null,
        ofi_id: this.ofi_id,
      };
      this.duplicateContactDraft = { firstName: data.first_name, lastName: data.last_name, email: data.email, mobile: data.mobile_no };

      // API call to create a new lead
      this.api.post('/ofi/listing/register-lead', data).subscribe({
        next: response => {
          const { data, message, status } = response;
          if (status) {
            this.insertedLead_id = data?.insertedId;
            this.register_submit = false;
            this.toaster.success(message);
            this.getAppointment();

            // Convert lead to contact if no existing contact ID
            if (!this.register_property.controls['contactid']?.value) {
              this.convertToContact(this.insertedLead_id);
            } else {
              this.registerModel = false;
              this.projectDetails(this.listing_details);
            }

            this.formReset();
          }
        },
      });
    } else if (method === 'update') {
      let check_name = this.register_property.controls['first_name'].value?.split(' ');
      let data = {
        first_name: check_name?.length > 0 ? check_name[0] : this.register_property.controls['first_name'].value || '',
        last_name: check_name?.length > 1 ? check_name[1] : '',
        mobile_no: this.register_property.controls['mobile_no'].value,
        email: this.register_property.controls['email'].value,
        project_details: this.register_property.controls['project_details'].value,
        send_link: false,
        note: this.custom_Editor2?.nativeElement['innerHTML'],
        attendee: false,
        like_dislike: null,
        lead_category: 'project',
        lead_status: 'New',
        lead_type: 'Buyer',
        project: this.listing_details?.projectid ? this.listing_details?.projectid : this.listing_details?._id,
        office_id: this.api.getOfficeId(),
        contactid: this.register_property.controls['contactid'].value ? this.register_property.controls['contactid'].value : null,
        ofi_id: this.ofi_id,
        note_id: this.updateRegister_user?.lead_notes?.length > 0 ? this.updateRegister_user?.lead_notes[0]._id : null,
      };

      // API call to update an existing lead
      this.api.put('/ofi/listing/register-lead/' + this.updateRegister_user?._id, data).subscribe({
        next: response => {
          const { data, message, status } = response;
          if (status) {
            this.insertedLead_id = data?.insertedId;
            this.register_submit = false;
            this.toaster.success(message);
            this.getAppointment();
            this.projectDetails(this.listing_details);
            this.updateRegister_user = null;
            this.formReset();
          }
        },
        error: () => {
          this.toaster.error('Something went wrong');
        },
      });
    }
  }

  formReset() {
    this.registerModel = false;
    this.register_property.reset();
    this.register_user_note = null;
    this.custom_Editor2.nativeElement['innerHTML'] = '';
    this.message.sendMessage({ isClearItems: true });
  }

  /**
   * Searches for leads based on the query and filters the lead list accordingly.
   */
  searchLead() {
    this.listing_details = this.listing_details_copy;
    let value = this.query?.toLowerCase();
    if (value) {
      let filterer_leads = this.listing_details?.leads?.filter(
        el =>
          el?.full_name?.toString()?.toLowerCase()?.includes(value) ||
          el?.email?.toString()?.toLowerCase()?.includes(value) ||
          el?.mobile_no?.toString()?.includes(value)
      );
      const filteredObject = {
        ...this.listing_details,
        leads: filterer_leads,
      };
      this.listing_details = filteredObject;
    } else {
      this.listing_details = this.listing_details_copy;
    }
  }

  /**
   * Converts a lead into a contact. If the contact does not exist, it creates a new contact.
   * @param lead - Lead to be converted
   * @param contactid - ID of the contact if already existing
   * @param createnew - Flag to create a new contact
   * @param isClicked - Flag to indicate user interaction
   */
  convertToContact(lead, contactId = null, createNew = false, _isClicked: boolean = false, addAsAlternate = false, alternateLabel: string = null) {
    this.contactConflict = false;
    if (lead?.contactid == '' || lead?.contactid == 'null' || lead?.contactid == null || lead?.hasOwnProperty('contactid') == false) {
      this.api
        .post(`/leads/convert-to-contact/${lead}`, {
          contactid: contactId,
          createnew: createNew,
          add_as_alternate: addAsAlternate,
          alternate_label: alternateLabel,
        })
        .subscribe({
          next: response => {
            const { message, status } = response;
            if (status) {
              this.registerModel = false;
              this.projectDetails(this.listing_details);
              if (response.hasOwnProperty('userexist') || response.hasOwnProperty('usersexist')) {
                this.cContacts = response.userexist ?? response.usersexist;
                this.conflictLeadId = lead;
                this.contactConflict = true;
              } else {
                this.toaster.success(message);
              }
            }
          },
        });
    }
  }

  // Starts voice recording and updates the note field with recognized text
  async startRecording() {
    this.voiceRecognition.stop();
    clearInterval(this.isRecording);
    this.isRecord = true;
    this.voiceRecognition.start();
    if (this.register_property.controls['note'].value) {
      this.voiceRecognition.text = this.register_property.controls['note'].value + '<br/>';
    }
    this.isRecording = setInterval(() => {
      if (this.voiceRecognition.text != undefined) {
        if (this.voiceRecognition.text) {
          this.register_property.patchValue({
            note: this.voiceRecognition.text,
          });
        }
      }
    }, 100);
  }

  // Stops voice recording if no words are recognized
  stopRecording() {
    if (this.voiceRecognition.tempWords == '') {
      if (this.isRecord) {
        this.voiceRecognition.stop();
        // The interval handle lives in `isRecording`; clearing the never-set `tm` left it
        // patching the note every 100ms after Stop.
        clearInterval(this.isRecording);
        this.voiceRecognition.text = '';
        this.isRecording = null;
        this.isRecord = false;
      }
    }
  }

  // Toggles the open/close state of a given box
  toggleBox(box): void {
    box.isOpen = !box.isOpen;
  }

  // Filters preference tags based on user input
  filterItem() {
    this.preference_tags = this.preference_tags_copy;
    let value = this.keywordTags?.toLowerCase();
    this.preference_tags = this.preference_tags?.map(el => {
      return { ...el, data: el?.data?.filter(x => x?.tag_name?.toLowerCase().includes(value)) };
    });
  }

  // Adds a tag to the form if it is not already attached
  addTagInForm(index: number, data) {
    let findInd = this.todo.find(el => el == data?.tag_name);
    if (!findInd) {
      this.todo?.splice(index, 0, data?.tag_name);
    } else {
      this.toaster.error('Tag is already attached');
    }
  }

  // Removes a public tag from the list
  removePublicTag(i) {
    this.todo?.splice(i, 1);
  }

  // Adds a new tag to the list if the input value is not empty
  addTag() {
    if (this.inputValue.trim() !== '') {
      this.todo.push(this.inputValue.trim());
      this.inputValue = '';
    }
  }

  // Closes the tag manager modal and resets the tag search
  closeTagManger() {
    this.lmService.tag_manager = false;
    this.resetTagSearch();
  }

  // Resets the keyword tag search input and restores the original tag list
  resetTagSearch() {
    this.keywordTags = null;
    this.preference_tags = this.preference_tags_copy;
  }

  // Adds a new tag to the array if it doesn't already exist
  pushTagToArray(e) {
    let tagArr = this.preference_tags.find(tagArr => tagArr.module_type == this.module_type?.name);
    if (tagArr?.data) {
      let tag_exist = tagArr?.data?.find(tag => tag?.tag_name?.toLowerCase().trim() == e.value.toLowerCase().trim());
      let tag_in_new_tag = [];
      if (this.new_tag?.length) {
        tag_in_new_tag = this.new_tag.filter(tag => tag?.toLowerCase().trim() == e.value.toLowerCase().trim());
      }
      if (tag_exist) {
        this.new_tag.splice(this.new_tag.indexOf(e.value.trim()), 1);
        this.toaster.error('Tag already exist');
      } else if (tag_in_new_tag?.length > 1) {
        this.getUnique();
        this.toaster.error('Tag is already entered');
      }
    }
  }

  // Removes duplicate tags from the new_tag array
  getUnique() {
    var uniqueArray = [];
    for (var value of this.new_tag) {
      if (uniqueArray.indexOf(value.toLowerCase().trim()) === -1) {
        uniqueArray.push(value.trim());
      }
    }
    this.new_tag = uniqueArray;
  }

  // Sends a request to create a new tag
  createTag() {
    let payLoad = {
      'tag_name[]': this.new_tag,
      module_id: this.module_Id,
      type_id: this.tag_type?._id,
      project_id: this.project_id,
      office_id: this.api.getOfficeId(),
      user_id: this.api.getUserId(),
    };
    this.api.post('/general/tag-manager/', payLoad).subscribe({
      next: data => {
        if (data?.status == true) {
          this.toaster.success('Tag successfully created');
          this.getProjectTags();
          this.new_tag = [];
          this.tag_type = null;
          this.submit = false;
          this.new_tag_popup = false;
        } else {
          this.toaster.error(data?.message);
        }
      },
    });
  }

  // Sends a request to create a new tag type
  createTagType() {
    let payLoad = {
      module_id: this.module_Id,
      type: 'tag_types',
      name: this.tag_type,
      project_id: this.project_id,
      office_id: this.api.getOfficeId(),
      user_id: this.api.getUserId(),
    };
    this.api.post('/general/tag-settings/', payLoad).subscribe({
      next: data => {
        if (data?.status == true) {
          this.toaster.success('Tag type successfully created');
          this.getProjectTags();
          this.tag_type = null;
          this.newTagTypePopup = false;
        } else {
          this.toaster.error(data?.message);
        }
      },
    });
  }

  // Checks if the selected tag type is 'Create New' and opens a popup for new tag type creation
  isNewTypeModel(event) {
    if (event?._id == 'createnew') {
      this.newTagTypePopup = true;
      this.tag_type = '';
    }
  }

  // ── List: filters, tabs, stats, sort, paging ───────────────────────────────
  // Every change below just re-requests the page; the list API does the filtering.

  /** Search box — debounced so typing doesn't fire a request per keystroke. */
  onSearchInput() {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.applyFilters(), 300);
  }

  /** Any filter, tab, sort, page-size or stats-period change: back to page 1 and reload. */
  applyFilters() {
    this.pageIndex = 0;
    this.getAppointment();
  }

  selectTab(tab: AppointmentTab) {
    this.activeTab = tab;
    this.applyFilters();
  }

  toggleSort() {
    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    this.applyFilters();
  }

  goToPage(index: number) {
    if (index < 0 || index >= this.pageCount) return;
    this.pageIndex = index;
    this.getAppointment();
  }

  get pageCount(): number {
    return Math.ceil(this.totalRecords / this.pageSize);
  }

  /** "1–10 of 31" footer label. */
  get pageRangeLabel(): string {
    const total = this.totalRecords;
    if (!total) return '0 of 0';
    const first = this.pageIndex * this.pageSize + 1;
    const last = Math.min(first + this.pageSize - 1, total);
    return `${first}–${last} of ${total}`;
  }

  get isPageChecked(): boolean {
    return this.pagedAppointments.length > 0 && this.pagedAppointments.every(appt => appt.is_checked);
  }

  togglePageChecked(checked: boolean) {
    this.pagedAppointments.forEach(appt => (appt.is_checked = checked));
    this.cancelApp();
  }

  /**
   * Row status if the API leaves `status` out (doc §3). Completed = saved `appt_status` of
   * 'completed', else attendance recorded; otherwise an appointment whose end time has passed
   * still needs updating, so it reads as Overdue rather than silently dropping out of Upcoming.
   */
  private getAppointmentStatus(appt: any): AppointmentStatus {
    if (appt?.appt_status === 'completed' || (!appt?.appt_status && appt?.total_attendee?.length > 0)) return 'completed';
    const end = moment(appt?.event?.end?.dateTime || appt?.event?.start?.dateTime);
    return end.isBefore(moment()) ? 'overdue' : 'upcoming';
  }

  /** ObjectIds embed their creation second — the list API has no created_at yet. */
  private getCreatedAt(id: string): Date | null {
    return /^[0-9a-f]{24}$/i.test(id ?? '') ? new Date(parseInt(id.substring(0, 8), 16) * 1000) : null;
  }

  /** Called per row on every change detection, so it reads a Map built once in getAgents(). */
  getStaffName(id: string): string {
    return this.staffNames.get(String(id)) ?? '';
  }

  private getInitials(name: string): string {
    return (name ?? '')
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map(part => part.charAt(0).toUpperCase())
      .join('');
  }

  /** Pill colour per appointment type; unknown types fall back to neutral. */
  private getTypeClass(type: string): string {
    return 'type-pill--' + (TYPE_PILL_COLORS[type?.toLowerCase()?.trim()] ?? 'gray');
  }

  getNameList(people: any[], from = 0): string {
    return people
      ?.slice(from)
      ?.map(person => person?.full_name)
      ?.join(', ');
  }
}

const TYPE_PILL_COLORS: Record<string, string> = {
  inspection: 'blue',
  'personal briefing': 'purple',
  'onsite training': 'orange',
  appointment: 'green',
};

export type AppointmentTab = 'all' | 'upcoming' | 'overdue' | 'completed';
export type AppointmentStatus = 'upcoming' | 'overdue' | 'completed';
export type StatsPeriod = 'week' | 'month' | 'year' | 'all';
